import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { THEMES, DEFAULT_THEME, getTheme } from '../data/themeConfig';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [currentTheme, setCurrentTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      loadUserTheme();
    } else {
      // Load from localStorage for non-logged in users
      const savedTheme = localStorage.getItem('nurseapp-theme');
      if (savedTheme) {
        const theme = getTheme(savedTheme);
        if (theme) {
          setCurrentTheme(theme);
        }
      }
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadUserTheme = async () => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const themeId = userData.theme || 'WHITE';
        const theme = getTheme(themeId);
        setCurrentTheme(theme);
      } else {
        // Set default theme
        setCurrentTheme(DEFAULT_THEME);
      }
    } catch (error) {
      console.error('Error loading theme:', error);
      setCurrentTheme(DEFAULT_THEME);
    } finally {
      setLoading(false);
    }
  };

  const setTheme = async (themeId) => {
    const theme = getTheme(themeId);
    if (!theme) {
      console.error('Invalid theme ID:', themeId);
      return;
    }

    setCurrentTheme(theme);
    
    // Save to localStorage
    localStorage.setItem('nurseapp-theme', themeId);

    // Save to user profile if logged in
    if (currentUser) {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          await updateDoc(userRef, {
            theme: themeId,
            updatedAt: new Date()
          });
        } else {
          await setDoc(userRef, {
            theme: themeId,
            createdAt: new Date(),
            updatedAt: new Date()
          });
        }
      } catch (error) {
        console.error('Error saving theme:', error);
      }
    }

    // Apply theme to document
    applyTheme(theme);
  };

  const applyTheme = (theme) => {
    const root = document.documentElement;
    
    // Set CSS variables
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-background', theme.colors.background);
    root.style.setProperty('--color-secondary-bg', theme.colors['secondary-bg']);
    root.style.setProperty('--color-text-primary', theme.colors['text-primary']);
    root.style.setProperty('--color-text-secondary', theme.colors['text-secondary']);
    root.style.setProperty('--color-border', theme.colors['border-color']);
    root.style.setProperty('--color-accent', theme.colors.accent);
    
    // Add theme class to body
    document.body.className = document.body.className
      .replace(/theme-\w+/g, '')
      .trim();
    document.body.classList.add(`theme-${theme.id.toLowerCase()}`);
  };

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme]);

  const value = {
    currentTheme,
    themes: Object.values(THEMES),
    setTheme,
    loading
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

